import { Employee } from "domain/entities/employee";
import { Parameter } from "domain/entities/parameter";
import { Timesheet } from "domain/entities/timesheet";
import { EMAIL } from "domain/enum/email";
import { EmployeeInputFilter } from "domain/models/employee-input-filter";
import { TimesheetInputFilter } from "domain/models/timesheet-input-filter";
import { TimesheetNotifyInputFilter } from "domain/models/timesheet-notify-input-filter";
import DropdownComponent from "presentation/components/Shared/DropdownComponent";
import InputDateComponent from "presentation/components/Shared/InputDateComponent";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import {
  useBlockAllTimesheetMutation,
  useCreateAllTimesheetMutation,
  useCreateTimesheetMutation,
  useTimesheetsByFilter,
  useTimesheetsNotifier,
  useUnlockAllTimesheetMutation,
  useUpdateTimesheetMutation,
} from "presentation/hooks/use-query-timesheet";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Menu } from "primereact/menu";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../index.css";
import TimesheetGrid from "../TimesheetGrid";
import Spinner from "presentation/components/Shared/Spinner";
import { TripsheetInputFilter } from "domain/models/tripsheet-input-filter";
import { useTripsheetsByFilter } from "presentation/hooks/use-query-tripsheet";

interface TimesheetSettingParams {
  date: Date;
  employee: Employee;
}

const TimesheetSetting: React.FC = () => {
  const { email: userEmployeeEmail, userPermissions } = useAuthStore();
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();

  const editedStatus = ["Editado", "Novo"];
  const finalStatus = ["Cancelado", "Aprovado", "Não Aprovado"];
  const allowedTripsheetStatusToUnlockTimesheet = ["Desbloqueado", "Cancelado pré Assinatura", "Cancelado pós Assinatura"]

  const confirmAction = (message: string, acceptCallback: () => void) => {
    confirmDialog({
      message: message,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: acceptCallback,
    });
  };

  // Estado para controlar qual filtro usar
  const [showOnlyActive, setShowOnlyActive] = useState<boolean>(true);

  // Usa o filtro baseado no estado
  const { data: employeesData } = useEmployeesByFilter({
    limit: 100,
    ...(showOnlyActive ? { isActive: true } : { isActive: false }),
  } as EmployeeInputFilter);

  const [employees, setEmployees] = useState<Parameter[]>();
  const [timesheetParameters, setTimesheetParameters] =
    useState<TimesheetSettingParams>();
  const [showModal, setShowModal] = useState(false);
  let currentMonth = timesheetParameters?.date
    ? new Date(timesheetParameters?.date).getMonth() + 1
    : new Date().getMonth() + 1;

  let currentYear = timesheetParameters?.date
    ? new Date(timesheetParameters?.date).getFullYear()
    : new Date().getFullYear();

  const [employeeToAnalyse, setEmployeeToAnalyse] = useState<Parameter>();
  const [hasEveryBlocked, setHasEveryBlocked] = useState<boolean>(false);

  const { data: existentTimesheets, refetch: refetchExistentTimesheets } =
    useTimesheetsByFilter({
      employeeId: employeeToAnalyse?.id ?? "",
      month: timesheetParameters?.date
        ? new Date(timesheetParameters?.date).getMonth() + 1
        : -1,
      year: timesheetParameters?.date
        ? new Date(timesheetParameters?.date).getFullYear()
        : 1900,
    } as TimesheetInputFilter);

  const {
    mutate: blockTimesheetMutation,
    error: blockingTimesheetError,
    isError: isBlockingTimesheetError,
    isSuccess: isBlockingTimesheetSuccess,
  } = useUpdateTimesheetMutation();

  const {
    mutate: unlockTimesheetMutation,
    error: unlockingTimesheetError,
    isError: isUnlockingTimesheetError,
    isSuccess: isUnlockingTimesheetSuccess,
  } = useUpdateTimesheetMutation();

  const {
    mutate: notifyTimesheetMutation,
    error: notifyTimesheetError,
    isError: isNotifyTimesheetError,
    isSuccess: isNotifyTimesheetSuccess,
  } = useTimesheetsNotifier();

  const {
    mutate: createTimesheetMutation,
    error: creationTimesheetError,
    isError: isCreationTimesheetError,
    isSuccess: isCreationTimesheetSuccess,
    data: timesheetCreated,
    isPending: isCreatePending,
  } = useCreateTimesheetMutation();

  // const {
  //   mutate: createAllTimesheetMutation,
  //   error: creationAllTimesheetError,
  //   isError: isCreationAllTimesheetError,
  //   isSuccess: isCreationAllTimesheetSuccess,
  //   data: allTimesheetCreated,
  //   isPending: isCreateAllPending,
  //   status: statusCreateAll,
  // } = useCreateAllTimesheetMutation();

  // const {
  //   mutate: blockAllTimesheetMutation,
  //   error: blockAllTimesheetError,
  //   isError: isBlockAllTimesheetError,
  //   isSuccess: isBlockAllTimesheetSuccess,
  // } = useBlockAllTimesheetMutation();

  // const {
  //   mutate: unlockAllTimesheetMutation,
  //   error: unlockAllTimesheetError,
  //   isError: isUnlockAllTimesheetError,
  //   isSuccess: isUnlockAllTimesheetSuccess,
  // } = useUnlockAllTimesheetMutation();

  const [employeeIdToCheckTripsheets, setEmployeeIdToCheckTripsheets] = useState("");
  const [monthToCheckTripsheets, setMonthToCheckTripsheets] = useState(0);
  const [yearToCheckTripsheets, setYearToCheckTripsheets] = useState(0);
  const [pendingTimesheet, setPendingTimesheet] = useState<Timesheet | null>(null);


  const {
    data: tripsheets,
    isLoading: isLoadingTripsheets,
    isError: isErrorLoadingTripsheets,
    error: errorLoadingTripsheets,
    refetch: refetchTripsheets,
  } = useTripsheetsByFilter({
    employeeId: employeeIdToCheckTripsheets,
    month: monthToCheckTripsheets,
    year: yearToCheckTripsheets
  } as TripsheetInputFilter);

  const handleBlock = async () => {
    if (existentTimesheets?.data) {
      const notBlockedTimesheets = existentTimesheets.data.filter((ts) =>
        editedStatus.includes(ts.status)
      );
      for (let ts of notBlockedTimesheets) {
        ts.lastModifiedBy = userEmployeeEmail ?? EMAIL;
        ts.status = "Enviado";
        blockTimesheetMutation(ts);
      }
    }
  };

  const handleUnlock = () => {
    if (existentTimesheets?.data) {
      const blockedTimesheets = existentTimesheets.data.filter(
        ts => !editedStatus.includes(ts.status)
      );

      const lastTimesheet = blockedTimesheets.sort((a, b) => a.version - b.version).pop();

      if (lastTimesheet) {
        setEmployeeIdToCheckTripsheets(lastTimesheet.employee.id);
        setMonthToCheckTripsheets(lastTimesheet.month);
        setYearToCheckTripsheets(lastTimesheet.year);

        setPendingTimesheet(lastTimesheet);
      }
    }
  };

  useEffect(() => {
    if (!pendingTimesheet) return;

    const fn = async () => {
      const { data: refetched } = await refetchTripsheets();

      const blockedTripsheets = refetched?.data?.filter(
        trsh => !allowedTripsheetStatusToUnlockTimesheet.includes(trsh.status)
      );

      if (blockedTripsheets?.length) {
        toast.current?.show({
          severity: "warn",
          summary: "Aviso",
          detail:
            "O mapa de horas não pode ser desbloqueado pois o mapa de kms deste mês já se encontra bloqueado. Se for necessário efetuar alguma alteração ao mapa de horas, deve ser criada uma nova versão.",
          life: 10000,
        });
      } else {
        pendingTimesheet.lastModifiedBy = userEmployeeEmail ?? EMAIL;
        pendingTimesheet.status = "Editado";
        unlockTimesheetMutation(pendingTimesheet);
      }
      setPendingTimesheet(null);
    };

    fn();
  }, [
    employeeIdToCheckTripsheets,
    monthToCheckTripsheets,
    yearToCheckTripsheets,
  ]);



  const handleCreate = async () => {
    if (timesheetParameters) {
      const month = new Date(timesheetParameters.date).getMonth() + 1;
      const year = new Date(timesheetParameters.date).getFullYear();

      const timesheetToCreate: Timesheet = {
        month: month,
        year: year,
        status: "Novo",
        employee: {
          id: employeeToAnalyse?.id || "",
          name: employeeToAnalyse?.value || "",
        },
        version: -1,
        lastModifiedBy: userEmployeeEmail ?? EMAIL,
      };
      //await handleBlock();
      createTimesheetMutation(timesheetToCreate);
    }
  };

  // const handleCreateAll = async () => {
  //   if (timesheetParameters) {
  //     const month = new Date(timesheetParameters.date).getMonth() + 1;
  //     const year = new Date(timesheetParameters.date).getFullYear();

  //     const timesheetsToCreate: Timesheet = {
  //       month: month,
  //       year: year,
  //       lastModifiedBy: userEmployeeEmail ?? EMAIL,
  //       status: "",
  //       employee: {
  //         id: "",
  //         name: "",
  //       },
  //       version: -1,
  //     };
  //     createAllTimesheetMutation(timesheetsToCreate);
  //   }
  // };

  const handleNotifyAll = async () => {
    if (timesheetParameters) {
      const month = new Date(timesheetParameters.date).getMonth() + 1;
      const year = new Date(timesheetParameters.date).getFullYear();

      const timesheetsToNotify: TimesheetNotifyInputFilter = {
        month: month,
        year: year,
      };
      notifyTimesheetMutation(timesheetsToNotify);
    }
  };

  // const handleBlockAll = async () => {
  //   if (timesheetParameters) {
  //     const month = new Date(timesheetParameters.date).getMonth() + 1;
  //     const year = new Date(timesheetParameters.date).getFullYear();

  //     const timesheetsToBlock: Timesheet = {
  //       month: month,
  //       year: year,
  //       lastModifiedBy: userEmployeeEmail ?? EMAIL,
  //       status: "",
  //       employee: {
  //         id: "",
  //         name: "",
  //       },
  //       version: -1,
  //     };
  //     blockAllTimesheetMutation(timesheetsToBlock);
  //   }
  // };

  // const handleUnlockAll = async () => {
  //   if (timesheetParameters) {
  //     const month = new Date(timesheetParameters.date).getMonth() + 1;
  //     const year = new Date(timesheetParameters.date).getFullYear();

  //     const timesheetsToUnlock: Timesheet = {
  //       month: month,
  //       year: year,
  //       lastModifiedBy: userEmployeeEmail ?? EMAIL,
  //       status: "",
  //       employee: {
  //         id: "",
  //         name: "",
  //       },
  //       version: -1,
  //     };
  //     unlockAllTimesheetMutation(timesheetsToUnlock);
  //   }
  // };

  useEffect(() => {
    const formatedEmployees = employeesData?.data?.map((item: Employee) => {
      const employee: Parameter = {
        id: item.id,
        value: `${item.name}${item.isActive ? "" : " (Inativo)"}`,
        type: "employee",
      };
      return employee;
    });

    const allEmployeesOption: Parameter = {
      id: "todos",
      value: "TODOS",
      type: "employee",
    };

    setEmployees([allEmployeesOption, ...(formatedEmployees ?? [])]);
  }, [employeesData, showOnlyActive]);

  useEffect(() => {
    if (timesheetParameters?.employee?.id && timesheetParameters?.date) {
      refetchExistentTimesheets();
      currentMonth = timesheetParameters?.date
        ? new Date(timesheetParameters?.date).getMonth() + 1
        : new Date().getMonth() + 1;

      currentYear = timesheetParameters?.date
        ? new Date(timesheetParameters?.date).getFullYear()
        : new Date().getFullYear();
    }
  }, [timesheetParameters, refetchExistentTimesheets, employeeToAnalyse]);

  useEffect(() => {
    if (timesheetParameters?.employee) {
      const empToAnalyse: Parameter = {
        id: timesheetParameters.employee.id,
        value: timesheetParameters.employee.name,
        type: "employee",
      };
      setEmployeeToAnalyse(empToAnalyse);
    }
  }, [timesheetParameters?.employee]);

  useEffect(() => {
    if (
      existentTimesheets?.data?.some((ts) => editedStatus.includes(ts.status))
    )
      setHasEveryBlocked(false);
    else setHasEveryBlocked(true);
  }, [existentTimesheets]);

  useEffect(() => {
    if (isCreationTimesheetError) {
      let message = [];
      message.push(creationTimesheetError.message);
      message = creationTimesheetError.response.data.metadata.errors;
      console.log("message", message);
      message.map((item: string) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: item || "Ocorreu um erro inesperado",
          life: 1500,
        });
      });
    }
  }, [creationTimesheetError, isCreationTimesheetError]);

  useEffect(() => {
    if (isBlockingTimesheetError) {
      let message = [];
      message.push(blockingTimesheetError.message);
      message = blockingTimesheetError.response.data.metadata.errors;
      console.log("message", message);
      message.map((item: string) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: item || "Ocorreu um erro inesperado",
          life: 1500,
        });
      });
    }
  }, [blockingTimesheetError, isBlockingTimesheetError]);

  useEffect(() => {
    if (isUnlockingTimesheetError) {
      let message = [];
      message.push(unlockingTimesheetError.message);
      message = unlockingTimesheetError.response.data.metadata.errors;
      console.log("message", message);
      message.map((item: string) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: item || "Ocorreu um erro inesperado",
          life: 1500,
        });
      });
    }
  }, [unlockingTimesheetError, isUnlockingTimesheetError]);

  // useEffect(() => {
  //   if (isCreationAllTimesheetError) {
  //     let message = [];
  //     message.push(creationAllTimesheetError.message);
  //     message = creationAllTimesheetError.response.data.metadata.errors;
  //     console.log("message", message);
  //     message.map((item: string) => {
  //       toast.current?.show({
  //         severity: "error",
  //         summary: "Erro",
  //         detail: item || "Ocorreu um erro inesperado",
  //         life: 1500,
  //       });
  //     });
  //   }
  // }, [creationAllTimesheetError, isCreationAllTimesheetError]);

  // useEffect(() => {
  //   if (isBlockAllTimesheetError) {
  //     let message = [];
  //     message.push(blockAllTimesheetError.message);
  //     message = blockAllTimesheetError.response.data.metadata.errors;
  //     console.log("message", message);
  //     message.map((item: string) => {
  //       toast.current?.show({
  //         severity: "error",
  //         summary: "Erro",
  //         detail: item || "Ocorreu um erro inesperado",
  //         life: 1500,
  //       });
  //     });
  //   }
  // }, [blockAllTimesheetError, isBlockAllTimesheetError]);

  // useEffect(() => {
  //   if (isUnlockAllTimesheetError) {
  //     let message = [];
  //     message.push(unlockAllTimesheetError.message);
  //     message = unlockAllTimesheetError.response.data.metadata.errors;
  //     console.log("message", message);
  //     message.map((item: string) => {
  //       toast.current?.show({
  //         severity: "error",
  //         summary: "Erro",
  //         detail: item || "Ocorreu um erro inesperado",
  //         life: 1500,
  //       });
  //     });
  //   }
  // }, [unlockAllTimesheetError, isUnlockAllTimesheetError]);

  useEffect(() => {
    if (isCreationTimesheetSuccess) {
      const successMessage = "Timesheet criada com sucesso";

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: successMessage,
        life: 1500,
      });
      refetchExistentTimesheets();

      setTimeout(() => 2000);
    }
  }, [isCreationTimesheetSuccess, refetchExistentTimesheets]);

  useEffect(() => {
    if (isBlockingTimesheetSuccess) {
      const successMessage = "Timesheet bloqueada com sucesso";

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: successMessage,
        life: 1500,
      });

      refetchExistentTimesheets();

      setTimeout(() => 2000);
    }
  }, [isBlockingTimesheetSuccess, refetchExistentTimesheets]);

  useEffect(() => {
    if (isUnlockingTimesheetSuccess) {
      const successMessage = "Timesheet desbloqueada com sucesso";

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: successMessage,
        life: 1500,
      });

      refetchExistentTimesheets();

      setTimeout(() => 2000);
    }
  }, [isUnlockingTimesheetSuccess, refetchExistentTimesheets]);

  // useEffect(() => {
  //   if (isCreationAllTimesheetSuccess) {
  //     const successMessage = "Timesheets criadas com sucesso";

  //     toast.current?.show({
  //       severity: "success",
  //       summary: "Sucesso",
  //       detail: successMessage,
  //       life: 1500,
  //     });

  //     refetchExistentTimesheets();

  //     setTimeout(() => 2000);
  //   }
  // }, [isCreationAllTimesheetSuccess, refetchExistentTimesheets]);

  // useEffect(() => {
  //   if (isBlockAllTimesheetSuccess) {
  //     const successMessage = "Timesheets bloqueadas com sucesso";

  //     toast.current?.show({
  //       severity: "success",
  //       summary: "Sucesso",
  //       detail: successMessage,
  //       life: 1500,
  //     });

  //     refetchExistentTimesheets();

  //     setTimeout(() => 2000);
  //   }
  // }, [isBlockAllTimesheetSuccess, refetchExistentTimesheets]);

  // useEffect(() => {
  //   if (isUnlockAllTimesheetSuccess) {
  //     const successMessage = "Timesheets desbloqueadas com sucesso";

  //     toast.current?.show({
  //       severity: "success",
  //       summary: "Sucesso",
  //       detail: successMessage,
  //       life: 1500,
  //     });

  //     refetchExistentTimesheets();

  //     setTimeout(() => 2000);
  //   }
  // }, [isUnlockAllTimesheetSuccess, refetchExistentTimesheets]);

  const menuRef = useRef<Menu>(null);

  const splitOptions = [
    // {
    //   label: "Criar",
    //   icon: "pi pi-plus",
    //   command: () =>
    //     confirmAction(
    //       "Deseja criar os mapas de horas para este mês?",
    //       handleCreateAll
    //     ),
    // },
    {
      label: "Notificar",
      icon: "pi pi-envelope",
      command: () =>
        confirmAction(
          "Deseja enviar e-mail aos utilizadores com mapa de horas pendente?",
          handleNotifyAll
        ),
    },
    // {
    //   label: "Bloquear",
    //   icon: "pi pi-lock",
    //   command: () =>
    //     confirmAction(
    //       "Deseja bloquear os mapas de horas deste mês?",
    //       handleBlockAll
    //     ),
    // },
    // {
    //   label: "Desbloquear",
    //   icon: "pi pi-lock-open",
    //   command: () =>
    //     confirmAction(
    //       "Deseja desbloquear as versões mais recentes dos mapas de horas deste mês?",
    //       handleUnlockAll
    //     ),
    // },
    {
      label: "Análise",
      icon: "pi pi-file",
      command: () =>
        navigate(`/timesheets/analysis/${currentYear}/${currentMonth}`),
      // command: () => setShowModal(true),
    },
    // {
    //   label: "Guardar Relatórios",
    //   icon: "pi pi-upload",
    //   command: () =>
    //     navigate(`/timesheets/reports/${currentYear}/${currentMonth}`),
    //   // command: () => setShowModal(true),
    // },
  ];

  const splitOptionsIndividual = [
    {
      label: "Criar nova versão",
      icon: "pi pi-plus",
      command: () =>
        confirmAction(
          "Deseja criar um novo mapa de horas para este mês?",
          handleCreate
        ),
    },
  ];

  useEffect(() => {
    if (
      existentTimesheets?.data?.length !== 1 ||
      (existentTimesheets?.data?.length === 1 &&
        !existentTimesheets?.data?.some((ts) =>
          finalStatus.includes(ts.status)
        ))
    )
      splitOptionsIndividual.push({
        label: hasEveryBlocked ? "Desbloquear" : "Bloquear",
        icon: hasEveryBlocked ? "pi pi-lock-open" : "pi pi-lock",
        command: () =>
          confirmAction(
            hasEveryBlocked
              ? "Deseja desbloquear o mapa de horas mais recente deste mês?"
              : "Deseja bloquear os mapas de horas deste mês?",
            hasEveryBlocked ? handleUnlock : handleBlock
          ),
      });
  }, [existentTimesheets, hasEveryBlocked]);

  const handleCloseModal = () => {
    setShowModal(false);
  };
  // useEffect(() => {
  //   console.log(statusCreateAll);
  // }, [statusCreateAll]);
  if (isCreatePending) return <Spinner />;

  const getMonthYear = () => {
    const month = timesheetParameters?.date
      ? new Date(timesheetParameters?.date).getMonth() + 1
      : new Date().getMonth() + 1;

    const year = timesheetParameters?.date
      ? new Date(timesheetParameters?.date).getFullYear()
      : new Date().getFullYear();

    return `${month}-${year}`;
  };

  return (
    <div className="timesheet-grid ">
      <div className="timesheet-2-cols">
        <div>
          <DropdownComponent
            options={employees ?? []}
            label="Colaborador"
            isValueId={true}
            fieldName="employee"
            currentObject={timesheetParameters}
            getValue={timesheetParameters?.employee?.id || null}
            setValue={setTimesheetParameters}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginTop: "8px",
              paddingLeft: "4px",
            }}
          >
            <Checkbox
              inputId="showOnlyActiveTimesheet"
              checked={showOnlyActive}
              onChange={(e) => setShowOnlyActive(e.checked ?? false)}
            />
            <label
              htmlFor="showOnlyActiveTimesheet"
              style={{
                fontSize: "13px",
                color: "#6c757d",
                cursor: "pointer",
                userSelect: "none",
                //Impede seleção de texto ao segurar
                WebkitUserSelect: "none",
                WebkitTouchCallout: "none",    
              }}
            >
              Mostrar apenas colaboradores ativos
            </label>
          </div>
        </div>
      </div>

      <div className="timesheet-1-col">
        <InputDateComponent
          label="Data"
          fieldName="date"
          currentObject={timesheetParameters}
          getValue={timesheetParameters?.date}
          setValue={setTimesheetParameters}
          view="month"
          dateFormat="yy/mm"
        />
      </div>

      <div className="timesheet-1-col mt-0 next-button">
        {/* Para colaborador específico */}
        {timesheetParameters?.employee?.id &&
          timesheetParameters?.employee?.id !== "todos" &&
          timesheetParameters.date && (
            <>
              {existentTimesheets?.data?.length ? (
                <>
                  <Menu model={splitOptionsIndividual} popup ref={menuRef} />
                  <Button
                    onClick={(e) => menuRef.current?.toggle(e)}
                    icon="pi pi-cog"
                    iconPos="left"
                    label="Opções"
                  >
                    <span className="flex align-items-center gap-2">
                      <i className="pi pi-chevron-down ml-2" />
                    </span>
                  </Button>
                </>
              ) : (
                <Button
                  label="Registar"
                  onClick={() =>
                    confirmAction(
                      "Deseja criar um mapa de horas para este mês?",
                      handleCreate
                    )
                  }
                  className="button-size"
                />
              )}
            </>
          )}

        {timesheetParameters?.employee?.id === "todos" &&
          timesheetParameters.date && (
            <>
              <Menu model={splitOptions} popup ref={menuRef} />
              <Button
                onClick={(e) => menuRef.current?.toggle(e)}
                icon="pi pi-cog"
                iconPos="left"
                label="Opções"
              >
                <span className="flex align-items-center gap-2">
                  <i className="pi pi-chevron-down ml-2" />
                </span>
              </Button>
            </>
          )}
      </div>

      <div className="timesheet-4-cols">
        {timesheetParameters?.employee &&
          timesheetParameters?.date &&
          timesheetParameters?.employee.id !== "todos" && (
            <TimesheetGrid
              externalFilters={{
                employeeId: employeeToAnalyse?.id ?? "",
                month: timesheetParameters?.date
                  ? new Date(timesheetParameters?.date).getMonth() + 1
                  : undefined,
                year: timesheetParameters?.date
                  ? new Date(timesheetParameters?.date).getFullYear()
                  : undefined,
                sortBy: "version",
                sortOrder: "desc",
              }}
              isDisable={true}
            />
          )}

        {timesheetParameters?.employee &&
          timesheetParameters?.date &&
          timesheetParameters?.employee.id === "todos" && (
            <TimesheetGrid
              externalFilters={{
                employeeId: "*",
                month: timesheetParameters?.date
                  ? new Date(timesheetParameters?.date).getMonth() + 1
                  : undefined,
                year: timesheetParameters?.date
                  ? new Date(timesheetParameters?.date).getFullYear()
                  : undefined,
                sortBy: "employee.name",
                sortOrder: "asc",
              }}
              isDisable={true}
            />
          )}
      </div>
      {/* <Dialog
        header={`Análise do Mapa de Horas: ${getMonthYear()}`}
        visible={showModal}
        style={{ width: "90vw", height: "90vh" }}
        onHide={handleCloseModal}
      >
        <TimesheetAnalysis
          month={
            timesheetParameters?.date
              ? new Date(timesheetParameters?.date).getMonth() + 1
              : new Date().getMonth() + 1
          }
          year={
            timesheetParameters?.date
              ? new Date(timesheetParameters?.date).getFullYear()
              : new Date().getFullYear()
          }
        />
      </Dialog> */}

      <Toast ref={toast} position="bottom-right" />

      <ConfirmDialog />
    </div>
  );
};
export default TimesheetSetting;
